import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { scrollToFirstInvalidControl } from '@common/scroll-to-first-invalid-control';
import { AddressInputsComponent } from '@components/address-inputs/address-inputs.component';
import { CardComponent } from '@components/card/card.component';
import { PaymentSubmitButtonComponent } from '@components/consultation-request/payment-submit-button/payment-submit-button.component';
import { FindTestsModalComponent } from '@components/find-tests-modal/find-tests-modal.component';
import { OrderAgreementsComponent } from '@components/order-agreements/order-agreements.component';
import { OrderMedicalHistoryComponent } from '@components/order-medical-history/order-medical-history.component';
import { OrderPhoneInputsComponent } from '@components/order-phone-inputs/order-phone-inputs.component';
import { OrderServiceAgreementComponent } from '@components/order-service-agreement/order-service-agreement.component';
import { OrderSummaryComponent } from '@components/order-summary/order-summary.component';
import { OrderUpgradeCardComponent } from '@components/order-upgrade-card/order-upgrade-card.component';
import { PatientInfoComponent } from '@components/patient-info/patient-info.component';
import { PaymentStepTabsComponent } from '@components/payment-step-tabs/payment-step-tabs.component';
import { TestCenterComponent } from '@components/test-center/test-center.component';
import { UpgradeComponent } from '@components/upgrade/upgrade.component';
import { WhatHappensNext } from '@components/what-happens-next/what-happens-next';
import { CustomerTestsTypes } from '@enums/customer-tests-types';
import { Sites } from '@enums/sites';
import { environment } from '@environments/environment';
import { ConsentAgreementRequiredError } from '@errors/consent-agreement-required-error';
import { IncompleteOrderFormError } from '@errors/incomplete-order-form-error';
import { InternationalCreditCardNotSupportedError } from '@errors/international-credit-card-not-supported-error';
import { LabRequiredError } from '@errors/lab-required-error';
import { ConfigService, FreePayment, GiftCardPayment, Payment, PaymentTypes } from '@medology/ngx-payment';
import { AnalyteCareLoadCartResponse } from '@models/analyte-care-load-cart-response';
import { MedicalHistory } from '@models/medical-history';
import { OrderResponse } from '@models/order-response';
import { OrderTest } from '@models/order-test';
import { PatientInfo } from '@models/patient-info';
import { Reorder } from '@models/reorder';
import { ShippingAddressStoreRequest } from '@models/requests/shipping-address-store.model';
import { TreatmentType } from '@models/treatment-types/treatment-type.model';
import { AccountService } from '@services/account.service';
import { BusinessAccountService } from '@services/business-account.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { BraintreeService } from '@services/external-payments/braintree.service';
import { FormService } from '@services/form.service';
import { LoadingService } from '@services/loading.service';
import { OrderService } from '@services/order.service';
import { PaymentService, provideDefaultPaymentConfig } from '@services/payment.service';
import { StorageService } from '@services/storage.service';
import { TreatmentTypeService } from '@services/treatment-type.service';
import { catchError, concatMap, firstValueFrom, Observable, of, Subscription, tap } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss'],
  imports: [
    OrderAgreementsComponent,
    OrderSummaryComponent,
    UpgradeComponent,
    TestCenterComponent,
    PatientInfoComponent,
    OrderMedicalHistoryComponent,
    PaymentStepTabsComponent,
    CardComponent,
    AddressInputsComponent,
    OrderPhoneInputsComponent,
    OrderServiceAgreementComponent,
    FindTestsModalComponent,
    OrderUpgradeCardComponent,
    PaymentSubmitButtonComponent,
    WhatHappensNext,
  ],
  providers: [provideDefaultPaymentConfig()],
})
export class OrderComponent implements OnInit, OnDestroy {
  @ViewChild('paymentStepTabs', { static: false }) public paymentStepTabsComponent: PaymentStepTabsComponent;

  public isLoading: boolean = true;
  public isCartStatic: boolean;
  public reorder: Reorder;
  public selectedPaymentType: PaymentTypes = PaymentTypes.CreditCard;
  public formValidationError: Error | null = null;
  public submissionErrors: (string | Error)[] = [];
  public processingOrder: boolean = false;
  public payments: Payment[] = [];

  private formValidationSubscription: Subscription | null = null;
  private isFormValidationActive: boolean = false;

  private readonly treatmentTypeService: TreatmentTypeService = inject(TreatmentTypeService);
  private storageService = inject(StorageService);
  private acRoute = inject(ActivatedRoute);
  private formService = inject(FormService);
  private errorHandlerService = inject(ErrorHandlerService);
  private accountService = inject(AccountService);
  private loadingService = inject(LoadingService);
  private braintreeService = inject(BraintreeService);
  private errorLoggingService = inject(ErrorLoggingService);
  private configService = inject(ConfigService);
  private orderService = inject(OrderService);
  private paymentService = inject(PaymentService);
  private businessAccountService = inject(BusinessAccountService);

  public constructor() {
    this.isCartStatic = environment.order.enableDynamicOrderUpgrade && !this.storageService.free;
    this.updatePaymentConfig();
  }

  /**
   * Get the address title based on the order type.
   *
   * The same address component is used for both free orders (displaying "Address" for patient's location) and shipping
   * orders like GLP-1 treatments (displaying "Shipping Address" for delivery location).
   */
  public get addressTitle(): string {
    return this.isUniversityFreeOrder ? 'Address' : 'Shipping Address';
  }

  /**
   * Gets the payment card title based on the order type.
   *
   * Subscription orders keep the payment method on file to bill future renewals, so it is collected as billing
   * information rather than a one-time payment.
   */
  public get paymentCardTitle(): string {
    return this.isSubscriptionOrder ? 'Enter Billing Information' : 'Enter Payment Information';
  }

  /**
   * Returns true when the order contains a test associated with a subscription plan.
   */
  public get isSubscriptionOrder(): boolean {
    return this.tests.some((test) => !!test.plan_id);
  }

  /**
   * Get the checkout form from the form service.
   */
  public get orderForm(): FormGroup {
    return this.formService.checkout;
  }

  /**
   * Returns true when has partner checkbox is checked
   */
  public get hasPartner(): boolean {
    return this.storageService.hasPartner;
  }

  /**
   * Gets the treatment type from the storage service.
   */
  public get treatmentType(): TreatmentType | null {
    return this.treatmentTypeService.getTreatmentType(this.storageService.treatmentType);
  }

  /**
   * Return if the domain requires parent consent form
   */
  public get shouldEnableParentConsentForm(): boolean {
    return this.isHealthLabs || !!this.storageService.inHomeBookingResult;
  }

  /**
   * Returns true when free order is set in localStorage
   */
  public get isFree(): boolean {
    return this.storageService.free;
  }

  /**
   * Gets whether this order is a free university order
   */
  public get isUniversityFreeOrder(): boolean {
    return this.isFree && !this.treatmentType;
  }

  /**
   * Get the address contact information form from the form service.
   */
  public get addressContactInformation(): FormGroup {
    return this.formService.addressContactInformation as FormGroup;
  }

  /**
   * Gets the address form, creating it if needed for non-free shipped orders.
   */
  public get addressForm(): FormGroup {
    if (this.isUniversityFreeOrder) {
      return this.addressContactInformation.get('address') as FormGroup;
    }

    if (!this.orderForm.get('address')) {
      this.createAddressFormControl();
    }

    return this.orderForm.get('address') as FormGroup;
  }

  /**
   * Returns true when isActive from baOrder is true in localStorage
   */
  public get isBaOrder(): boolean {
    return this.storageService.baOrder?.isActive;
  }

  /**
   * Returns true when the domain is Healthlabs
   */
  public get isHealthLabs(): boolean {
    return (environment.site as Sites) === Sites.Healthlabs;
  }

  /**
   * Returns true if the order upgrade feature is enabled for this site.
   */
  public get isOrderUpgradeEnabled(): boolean {
    return environment.order.enableOrderUpgrade;
  }

  /**
   * Gets whether the In-home Collection feature is allowed for this site.
   */
  public get isInHomeCollectionAllowed(): boolean {
    return environment.ngFindalab.inHomeCollectionAllowed;
  }

  /**
   * Gets whether the terms of service checkbox should be displayed on the page.
   */
  public get isTosOnOrderPageEnabled(): boolean {
    return environment.order.enableTosOnOrderPage;
  }

  /**
   * Gets the tests from storage.
   */
  public get tests() {
    return this.storageService.tests;
  }

  /**
   * Gets the header title and subtitle.
   *
   * If the current treatment defines a custom subtitle, it overrides the config value.
   */
  public get orderPageHeader(): { title: string; subtitle: string } {
    const header = environment.titles.orderPageHeader;
    const treatmentSubtitle = this.treatmentType?.orderPageHeaderSubtitle;

    return treatmentSubtitle ? { ...header, subtitle: treatmentSubtitle } : header;
  }

  /**
   * Whether the order is shipped.
   */
  public get isShippedOrder(): boolean {
    return this.storageService.isShipped;
  }

  /**
   * Returns true if the dynamic order upgrade feature is enabled for this site.
   */
  public get isDynamicUpgradeEnabled(): boolean {
    return environment.order.enableDynamicOrderUpgrade;
  }

  /**
   * Gets the text to display on the place order button.
   */
  public get submitButtonText(): string {
    return environment.buttons.checkout;
  }

  /**
   * Gets whether the pay later tab is selected from the payment step tabs component.
   */
  public get isPayLater(): boolean {
    return !this.paymentStepTabsComponent?.isPayNowTabSelected;
  }

  /**
   * Initializes the component by setting up form defaults, loading payment data, and handling reorder scenarios.
   */
  public ngOnInit(): void {
    this.setLoadingState(true);
    this.initializeFormDefaults();
    this.loadOrderData().subscribe(() => this.setLoadingState(false));
  }

  /**
   * Cleans up subscriptions when the component is destroyed.
   */
  public ngOnDestroy(): void {
    this.formValidationSubscription?.unsubscribe();
  }

  /**
   * Updates the payment methods accordingly.
   *
   * @param {Payment[]} payments the selected payment methods emitted from the payment step tabs component
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
    this.submissionErrors = [];

    if (this.isFormValidationActive || this.paymentService.hasExternalPayment(this.payments)) {
      // Run after change detection
      setTimeout(() => this.setupFormValidationListener());
    }

    const giftCardPayment = this.payments.find((p) => p instanceof GiftCardPayment);

    this.orderService.applyGiftCard(giftCardPayment ? giftCardPayment.balance : 0);
  }

  /**
   * Re-evaluates the submit validation error once the payment form becomes fully valid.
   */
  public onPaymentFormValid(): void {
    if (!this.isFormValidationActive) {
      return;
    }

    this.formValidationError = this.getFormValidationError();
  }

  /**
   * Handles the payment submit button click.
   */
  public onPaymentSubmit(): void {
    this.submissionErrors = [];
    document.querySelector('input[name="sex"]:checked')?.dispatchEvent(new Event('change'));
    this.orderForm.updateValueAndValidity();
    this.formValidationError = this.getFormValidationError();

    if (this.formValidationError) {
      this.setupFormValidationListener();
      scrollToFirstInvalidControl();

      return;
    }

    this.submitOrder();
  }

  /**
   * Gets the validation error message for the order form submission.
   *
   * @returns {string | null} the validation error message to display on the payment submit button
   */
  public getFormValidationError(): Error | null {
    const paymentFormValid = this.paymentStepTabsComponent?.validatePaymentForm() || this.isPayLater;
    const termsControl = this.orderForm.get('terms');

    if (this.orderForm.get('lab_id')?.invalid) {
      return new LabRequiredError();
    }

    if (termsControl && !termsControl.value) {
      return new ConsentAgreementRequiredError();
    }

    if (
      (this.isUniversityFreeOrder && this.addressContactInformation?.invalid) ||
      !this.orderForm.valid ||
      !paymentFormValid ||
      (!this.payments.length && !this.isPayLater)
    ) {
      return new IncompleteOrderFormError();
    }

    return null;
  }

  /**
   * Return the total amount provided from order service
   */
  public getTotalAmount(): number {
    return this.orderService.getTotal() / 100;
  }

  /**
   * Validates and submits the current order.
   *
   * Handles free orders, gift card secondary payments, and unsupported credit cards.
   *
   * @returns {Promise<void>} resolves when the order has been placed or an error has been handled
   */
  public async submitOrder(): Promise<void> {
    const validationErrors = this.orderService.getOrderSubmitValidationErrors();

    if (validationErrors.length) {
      this.submissionErrors = validationErrors;

      return;
    }

    if (this.paymentService.hasUnsupportedCreditCard(this.payments)) {
      this.submissionErrors = [new InternationalCreditCardNotSupportedError(environment.phone).message];

      return;
    }

    this.processingOrder = true;

    let orderResponse: OrderResponse;
    try {
      orderResponse = await firstValueFrom(
        this.orderService
          .submitOrder(this.braintreeService.deviceData, this.payments, this.isSubscriptionOrder)
          .pipe(tap((response) => this.storeShippingAddress(response))),
      );
    } catch (error) {
      this.handleErrorResponse(error);

      return;
    }

    const secondaryPayment = this.paymentService.getGiftCardAsSecondaryPayment(this.payments);

    if (secondaryPayment && !this.isSubscriptionOrder) {
      await this.processSecondaryPayment(orderResponse.transaction_id, secondaryPayment);
    }

    this.orderService.afterOrderPlacement(orderResponse);
  }

  /**
   * Gets the medical history from the reorder
   */
  public getReorderMedicalHistory(): MedicalHistory | null {
    if (!this.reorder) {
      return null;
    }

    return {
      prescriptions: this.reorder.consultation.prescriptions,
      allergies: this.reorder.consultation.allergies,
      health_info: this.reorder.consultation.health_info,
    };
  }

  /**
   * Gets the patient info from the reorder
   */
  public getReorderPatientInfo(): PatientInfo | null {
    if (!this.reorder) {
      return null;
    }

    const dob = new Date(this.reorder.dob.date);
    const accountType = this.reorder.email ? 'email' : this.reorder.phone ? 'phone' : 'none';
    const isConsultation = !!this.reorder?.consultation?.treatment_type;

    return {
      firstName: this.reorder.first_name,
      lastName: this.reorder.last_name,
      sex: this.reorder.sex ?? 'M',
      birthday: {
        month: dob.getMonth() + 1,
        day: dob.getDate(),
        year: dob.getFullYear(),
      },
      phone: isConsultation || accountType === 'phone' ? this.reorder.phone : null,
      email: isConsultation || accountType === 'email' ? this.reorder.email : null,
      contactVia: isConsultation ? null : accountType,
      voicemail_allowed: isConsultation ? null : this.reorder.leave_msg === '1' && accountType === 'phone',
    };
  }

  /**
   * Resets the payment form to Pay Now.
   */
  public resetPaymentFormToPayNow(): void {
    this.paymentStepTabsComponent?.resetPaymentFormToPayNow();
  }

  /**
   * Handle reorder
   *
   * @param {string} reorderId The transaction id of the order
   * @param {string} token     The JWT token
   */
  protected handleReorder(reorderId: string, token: string): Observable<any> {
    return this.orderService
      .getReorder(reorderId, token)
      .pipe(
        concatMap((reorder) =>
          this.orderService
            .loadCartRequest(this.orderService.getOrderRequestFromReorder(reorder))
            .pipe(map(() => reorder)),
        ),
      );
  }

  /**
   * Updates the to the ngx-payment payment config during init.
   *
   * External subscription orders always require a secondary payment method when a gift card is applied, even when the
   * gift card covers the order total, so a billing method is kept on file for future renewals.
   */
  private updatePaymentConfig(): void {
    this.configService.updatePaymentConfig({
      giftCardConfig: {
        displayName: `${environment.site}.com Gift Card`,
        apiUrl: `${environment.analyteCareApi}/api/v1/gift-card-balance`,
        alwaysRequireSecondaryPayment: this.isSubscriptionOrder,
      },
    });
  }

  /**
   * Sets the loading state for the component and toggles the global loader.
   *
   * @param {boolean} isLoading whether the component is in a loading state
   */
  private setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;
    this.loadingService.toggleLoader(isLoading);
  }

  /**
   * Loads reorder data based on the provided reorder ID.
   *
   * @param {string} reorderId the reorder transaction ID from the query parameter
   *
   * @returns {Observable<Reorder | null>} observable of the reorder data or null if no reorder
   */
  private loadReorderData(reorderId: string): Observable<Reorder | null> {
    this.orderService.clearLastOrder();

    return this.handleReorder(reorderId, this.storageService.authToken).pipe(
      tap((reorder: Reorder) => {
        if (reorder) {
          this.storageService.reorderId = reorderId;
          this.handleReorderResponse(reorder);
        }
      }),
      catchError((error) => this.handleInitializationError(error)),
    );
  }

  /**
   * Handles errors during order initialization. Logs the error and returns a safe observable to continue the stream.
   *
   * @param error The error that occurred during initialization
   *
   * @returns Observable of null to allow graceful error handling
   */
  private handleInitializationError(error: any): Observable<null> {
    this.errorLoggingService.logError(error);

    return of(null);
  }

  /**
   * Stores a shipping address for the placed order.
   *
   * @param {OrderResponse} orderResponse the response object from the order placement API
   */
  private storeShippingAddress(orderResponse: OrderResponse): void {
    if (!this.isShippedOrder || this.isThirdPartyFulfilled()) {
      return;
    }

    const { firstName: name, lastName: last_name, phone } = this.orderForm.get('patient').value;

    this.orderService
      .storeShippingAddress(
        new ShippingAddressStoreRequest(
          this.addressForm.value,
          { name, last_name, phone },
          orderResponse.tests.filter((test) => test.test_id).map((test) => test.customer_tests_id),
          this.treatmentType?.shippingService ?? '',
        ),
        orderResponse.transaction_id,
        orderResponse.hash,
      )
      .subscribe({ error: (error) => this.errorLoggingService.logError(error) });
  }

  /**
   * Handles the error response from a failed API request.
   *
   * @param {any} error the error response object received from the API
   */
  private handleErrorResponse(error: any): void {
    this.submissionErrors = this.errorHandlerService.handleResponseError(error);
    this.processingOrder = false;

    if (this.errorHandlerService.isCreditCardDeclineError(error)) {
      return;
    }

    this.errorLoggingService.logError(error);
  }

  /**
   * Processes the secondary payment using a gift card.
   *
   * @param {string} transactionId the transaction ID of the order that should be paid
   * @param {GiftCardPayment} giftCardPayment the gift card payment method containing the balance to apply
   */
  private async processSecondaryPayment(transactionId: string, giftCardPayment: GiftCardPayment): Promise<void> {
    try {
      const transactionResponse = await this.orderService.makeSecondaryPaymentWithGiftCard(
        transactionId,
        giftCardPayment,
      );
      this.storageService.secondaryPaymentTransactionId = transactionResponse.transaction_id;
    } catch (error) {
      this.errorLoggingService.logError(error);
    }
  }

  /**
   * Handle enabling customized In-Home Collection provider IDs when the query param is present
   */
  private handleInHomeCollectionProviderIds(): void {
    const providerIds = this.acRoute.snapshot.queryParams['ihc-providers'];

    this.storageService.providerIds = providerIds
      ? providerIds.split(',').map((id) => parseInt(id))
      : environment.ngFindalab.inHomeCollectionProviderIds;
  }

  /**
   * Creates and adds the address form control to the order form.
   */
  private createAddressFormControl(): void {
    this.orderForm.addControl(
      'address',
      new FormGroup({
        ...this.formService.address.controls,
        streetAddress2: new FormControl(''),
      }),
    );
  }

  /**
   * Handle the reorder response
   *
   * @param {Reorder} response The reorder response
   */
  private handleReorderResponse(response: Reorder): void {
    this.reorder = response;

    if (!this.storageService.zipcode && response.center?.zip_code) {
      this.storageService.zipcode = response.center.zip_code;
    }

    if (this.reorder?.consultation?.treatment_type) {
      this.storageService.treatmentType = this.reorder.consultation.treatment_type;
      this.setReorderExtraCharges(response.tests);
    }
  }

  /**
   * Enables Recharge in the payment config and sets it as active once it becomes selectable.
   *
   * @param {string} displayName the display name for the recharge payment option
   */
  private configureRechargePayment(displayName: string): void {
    this.paymentService.enableRecharge(this.configService, displayName);
    this.selectedPaymentType = PaymentTypes.Recharge;
  }

  /**
   * Sets extra charges from reorder response
   *
   * @param {OrderTest[]} customerTests The customer tests from the reorder response
   */
  private setReorderExtraCharges(customerTests: OrderTest[]): void {
    const extraChargeIdentifier = 'Extra Charge - ';

    this.storageService.extraCharges = customerTests
      .filter((item) => item.customer_tests_type === CustomerTestsTypes.ExtraCharge)
      .map((item) => {
        return {
          name: item.customer_tests_name.replace(extraChargeIdentifier, ''),
          amount: item.customer_tests_price,
        };
      });
  }

  /**
   * Sets initial form values and storage configurations.
   */
  private initializeFormDefaults(): void {
    this.handleInHomeCollectionProviderIds();
    this.storageService.ecommerceTrackingCategories = [];
    this.storageService.authToken = this.storageService.getCookie('account_token');

    if (this.isShippedOrder) {
      this.orderForm.get('lab_id').setValue(this.storageService.center?.id || this.orderService.getOrderLabId());
    }

    if (this.isFree) {
      this.onPaymentsSelected([new FreePayment()]);
    }

    if (this.isUniversityFreeOrder) {
      this.addressContactInformation.get('tos').setValue(true);
    }
  }

  /**
   * Orchestrates the loading of order data including payment info, reorder data, and cart.
   *
   * @returns {Observable<Reorder | AnalyteCareLoadCartResponse | null>} observable that completes when all data is loaded
   */
  private loadOrderData(): Observable<Reorder | AnalyteCareLoadCartResponse | null> {
    return this.handleBusinessAccountSetup().pipe(
      switchMap(() => this.loadPaymentRechargeData()),
      switchMap(() => this.handleReorderFlow()),
      switchMap((reorder) => (reorder ? of(reorder) : this.orderService.loadCart())),
      catchError((error) => this.handleInitializationError(error)),
    );
  }

  /**
   * Handles business account setup by checking if the account is a business account and setting the payment method to
   * Free if applicable.
   *
   * @returns {Observable<boolean>} observable that emits whether the account is a business account
   */
  private handleBusinessAccountSetup(): Observable<boolean> {
    return this.accountService.isBusinessAccount(this.storageService.authToken).pipe(
      switchMap((baId) => (baId ? this.resolveBusinessAccount(baId) : this.markOrderAsNonBusinessAccount())),
      tap(() => {
        if (this.storageService.baOrder.isActive) {
          this.onPaymentsSelected([new FreePayment()]);
        }
      }),
      catchError(() => this.markOrderAsNonBusinessAccount()),
    );
  }

  /**
   * Fetches BA details and sets the order as a business account order with fee waivers if applicable.
   * Falls back to activating without fee waivers if the details request fails.
   */
  private resolveBusinessAccount(baId: number): Observable<boolean> {
    return this.businessAccountService.getBusinessAccount(baId, this.storageService.authToken).pipe(
      tap((response) => {
        this.storageService.baOrder = {
          isActive: true,
          isPhysicianFeeWaived: !!response.is_physician_fee_waived,
          isStateComplianceFeeWaived: !!response.is_state_compliance_fee_waived,
        };
      }),
      map(() => true),
      catchError(() => {
        this.storageService.baOrder = {
          isActive: true,
          isPhysicianFeeWaived: false,
          isStateComplianceFeeWaived: false,
        };

        return of(true);
      }),
    );
  }

  /**
   * Marks the current session as a non-business account order.
   */
  private markOrderAsNonBusinessAccount(): Observable<boolean> {
    this.storageService.baOrder = {
      isActive: false,
      isPhysicianFeeWaived: false,
      isStateComplianceFeeWaived: false,
    };

    return of(false);
  }

  /**
   * Loads payment rechargeable data if auth token exists.
   *
   * @returns {Observable<RechargeRequest | null>} observable that emits payment recharge data or null
   */
  private loadPaymentRechargeData(): Observable<any> {
    const token = this.storageService.authToken;

    if (!token || this.storageService.baOrder?.isActive) {
      return of(null);
    }

    return this.orderService.getPaymentRechargeable(token).pipe(
      tap((rechargeData) => {
        if (!rechargeData.recharge) {
          return;
        }

        this.orderService.rechargeData = rechargeData;
        this.configureRechargePayment(`Recharge: ${rechargeData.recharge.detail.display_name}`);
      }),
      catchError((error) => this.handleInitializationError(error)),
    );
  }

  /**
   * Loads reorder data if both reorderId and authToken are present.
   *
   * @returns {Observable<Reorder | null>} observable that emits reorder data or null
   */
  private handleReorderFlow(): Observable<Reorder | null> {
    const reorderId = this.acRoute.snapshot.queryParams.reorderId;
    const token = this.storageService.authToken;

    if (!reorderId || !token) {
      return of(null);
    }

    return this.loadReorderData(reorderId);
  }

  /**
   * Resets the form validation subscription.
   *
   * This ensures we always subscribe to the correct form components when payments change.
   */
  private resetFormValidationSubscription(): void {
    this.formValidationSubscription?.unsubscribe();
    this.formValidationSubscription = null;
  }

  /**
   * Sets up a listener to track form value changes and update the submit validation error message.
   */
  private setupFormValidationListener(): void {
    this.isFormValidationActive = true;
    this.resetFormValidationSubscription();
    this.orderForm.markAllAsTouched();

    if (this.isUniversityFreeOrder && this.addressContactInformation.invalid) {
      this.addressContactInformation.markAllAsTouched();
    }

    this.formValidationError = this.getFormValidationError();

    this.formValidationSubscription = this.orderForm.valueChanges.subscribe(
      () => (this.formValidationError = this.getFormValidationError()),
    );
  }

  /**
   * Determines if the current treatment type is fulfilled by a third party, such as female condoms.
   */
  private isThirdPartyFulfilled(): boolean {
    return this.treatmentType?.isThirdPartyFulfilled ?? false;
  }
}
